import type { HashContent } from './controller/HashManager';
import { readEventMemory, writeEventMemory } from './eventMemory';
import { correctedNow } from './utils/clock';

const STORAGE_KEY = 'racemap-player-position';
/**
 * Long enough to cover a reload or the app killing the tab, short enough that coming back to a
 * still running race the next day starts at the live edge instead of yesterday's position.
 */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

/** `time: null` records that the viewer sat at the live edge — there is nothing to restore. */
type StoredPosition = { time: number | null; savedAt: number };

function isStoredPosition(value: unknown): value is StoredPosition {
  if (value == null || typeof value !== 'object') return false;
  const { time, savedAt } = value as Partial<StoredPosition>;

  return (time === null || typeof time === 'number') && typeof savedAt === 'number';
}

/** Remembers where the viewer is watching, so a reload can pick it up again. */
export function writePosition(eventId: string, timer: { time: number; isLive: boolean }): void {
  writeEventMemory<StoredPosition>(STORAGE_KEY, eventId, {
    time: timer.isLive ? null : timer.time,
    savedAt: correctedNow(),
  });
}

/** The timestamp the player should start at, or `null` to keep its default. */
export function getPositionToRestore(
  hash: Pick<HashContent, 'time'>,
  eventId: string,
  eventEndTime: number,
): number | null {
  // A time in the url wins, the same way a selection in the url beats the stored favorites.
  // parseHash yields NaN for an unparsable value, which is no instruction at all.
  if (hash.time != null && !Number.isNaN(hash.time)) return null;

  const stored = readEventMemory(STORAGE_KEY, eventId, isStoredPosition);
  if (stored?.time == null) return null;
  if (correctedNow() - stored.savedAt > MAX_AGE_MS) return null;
  // At the end there is nothing left to play: setTimerTime would clamp to the end and the first
  // tick would pause the player on the final frame. Falling back to the default is friendlier —
  // for a finished event that default is the event start.
  if (stored.time >= eventEndTime) return null;

  return stored.time;
}
